/* eslint-disable no-nested-ternary */
import type {
  ProListPageControllerFormValues,
  ProListPageControllerProps,
} from '@application/components';
import { PreloadController, SeoController } from '@application/components';
import {
  encodeUri,
  useModal,
  usePaginationSearch,
  useProSearch,
  useRouter,
  useTranslatedUrl,
} from '@application/utils';
import type { EducatorTypeEnum } from '@gql/graphql';
import { ProfessionalTypeEnum } from '@gql/graphql';
import type { ProListPageData } from '@presentation/pages';
import { ProListPage } from '@presentation/pages';
import type { MapCoordinates } from '@presentation/pages/ProList/components/Map/types';
import { MapMarkerLoading } from '@presentation/pages/ProList/components/MapMarkerLoading';
import { MAP_BREAKPOINT } from '@presentation/pages/ProList/styled';
import { FormikProvider, useFormik } from 'formik';
import { omit } from 'lodash/fp';
import hash from 'object-hash';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useGetLocationCountry } from '@application/api';
import { useGetProMap } from './api/useGetProMap';
import { useGetProMapMarker } from './api/useGetProMapMarker';
import type { UseGetProsParams } from './api/useGetPros';
import { useGetPros } from './api/useGetPros';
import { useGetProsMap } from './api/useGetProsMap';
import {
  DISTANCE_TO_ZOOM_MAP,
  EDUCATOR_MAP_INITIAL_VALUES,
  PROS_MAP_INITIAL_VALUES,
} from './constants';
import { useFormatMapMarker } from './useFormatMapMarker';
import { useFormatPros } from './useFormatPros';
import { useSearch } from './useSearch';
import { useSeo } from './useSeo';

export * from './types';

export const ProListPageController: FC<ProListPageControllerProps> = (props) => {
  /**
   * State / DI
   */

  const { type } = props;
  const { countryCode, infoFromCurrentPath } = useRouter();
  const proMapInitialValues = useMemo(() => {
    if (type === ProfessionalTypeEnum.EDUCATOR) {
      return EDUCATOR_MAP_INITIAL_VALUES[countryCode];
    }
    return PROS_MAP_INITIAL_VALUES[countryCode];
  }, [countryCode, type]);

  const { country } = useGetLocationCountry({
    location: infoFromCurrentPath.params[0] ? decodeURI(infoFromCurrentPath.params[0]) : undefined,
  });
  const { setSearchFromValues, valuesFromSearch, getSearchFromValues, formatEducatorTypes } =
    useSearch();
  const { currentPage, handlePaginationChange } = usePaginationSearch();

  const mapBreakpoint = useMediaQuery({ query: `(max-width: ${MAP_BREAKPOINT}px)` });

  useTranslatedUrl();

  const { formatPros, getImagesToPreload } = useFormatPros();

  const proSearch = useProSearch({
    setSearchOnUpdate: true,
    initialValues: {
      proType: type,
    },
  });

  const { headerTitle, seoData, canonicalUrl } = useSeo({
    proSearch,
    proType: type,
    locationCountry: country,
  });

  const { modal: mapModal, showFromClick: showMapModal } = useModal();
  const { modal: filtersModal, showFromClick: showFilters } = useModal();
  const [selectedProId, setSelectedProId] = useState<number | undefined>(undefined);

  const { formatMarker, formatPopup } = useFormatMapMarker({
    proType: type,
    onProClick: setSelectedProId,
  });
  const initialValues = {
    ...proMapInitialValues,
    ...valuesFromSearch,
  };

  const [lastSavedValues, setLastSavedValues] =
    useState<ProListPageControllerFormValues>(initialValues);

  const form = useFormik<ProListPageControllerFormValues>({
    initialValues,
    onSubmit: (values) => {
      setSearchFromValues(values);
      setLastSavedValues(values);
      window?.scrollTo(0, 0);
    },
  });

  /**
   * Helper variables
   */

  const searchCoordinates = useMemo((): MapCoordinates | undefined => {
    if (!proSearch.place) return;
    return {
      lat: parseFloat(proSearch.place.lat),
      lng: parseFloat(proSearch.place.lon),
    };
  }, [proSearch.place]);

  const prosMapBounds = useMemo(() => {
    if (proSearch.params.location || !lastSavedValues?.bounds) {
      return form.values?.bounds;
    }
    return lastSavedValues?.bounds;
  }, [form.values?.bounds, lastSavedValues?.bounds, proSearch.params.location]);

  const getProsParams = useMemo((): UseGetProsParams => {
    const params: UseGetProsParams = {
      type,
      page: currentPage,
      serviceId: proSearch.values.search,
      hasBooking: lastSavedValues.hasBooking,
      spokenLanguages: lastSavedValues.spokenLanguages,
      educatorTypes: formatEducatorTypes(lastSavedValues.educatorTypes as EducatorTypeEnum[]),
    };
    params.bounds = lastSavedValues.bounds;
    params.shouldUseLocationSearch =
      !!searchCoordinates || !!lastSavedValues.maxDistance || !!proSearch.params.location;
    if (typeof window === 'undefined') {
      const searchAddress = proSearch.params.location;
      if (searchAddress) {
        params.nearByAddress = encodeUri(searchAddress);
      }
    } else if (searchCoordinates || lastSavedValues) {
      params.nearByPoint = searchCoordinates
        ? {
            lat: searchCoordinates.lat,
            lng: searchCoordinates.lng,
          }
        : lastSavedValues.mapCenter;
      // Only use address in SSR
    }
    if (valuesFromSearch.maxDistance) {
      params.maxDistance = Math.floor(valuesFromSearch.maxDistance);
    }
    return params;
  }, [
    currentPage,
    formatEducatorTypes,
    lastSavedValues,
    proSearch.params.location,
    proSearch.values.search,
    searchCoordinates,
    type,
    valuesFromSearch.maxDistance,
  ]);

  const hasValuesChanged = useMemo(() => {
    const hashLastSavedValues = hash(
      omit(['maxDistance', 'hasBooking', 'spokenLanguages', 'educatorTypes'], lastSavedValues),
    );
    const hashCurrentValues = hash(
      omit(['maxDistance', 'hasBooking', 'spokenLanguages', 'educatorTypes'], form.values),
    );
    return hashLastSavedValues !== hashCurrentValues;
  }, [form.values, lastSavedValues]);

  const formValuesChanged = useMemo(() => {
    const hashInitialValues = hash(omit(['zoom', 'mapCenter', 'bounds'], proMapInitialValues));
    const hashCurrentValues = hash(omit(['zoom', 'mapCenter', 'bounds'], form.values));
    return hashCurrentValues !== hashInitialValues;
  }, [form.values, proMapInitialValues]);

  /**
   * API
   */

  const { proMap, proMapLoading } = useGetProMap(mapBreakpoint ? selectedProId : undefined);
  const { proMapMarker, proMapMarkerLoading } = useGetProMapMarker(
    mapBreakpoint ? undefined : selectedProId,
  );

  const { pros, pageCount, prosLoading } = useGetPros(getProsParams);

  const { prosMap, prosMapLoading } = useGetProsMap({
    type,
    serviceId: proSearch.values.search,
    bounds: prosMapBounds,
    zoom: lastSavedValues.zoom,
    hasBooking: lastSavedValues.hasBooking,
    spokenLanguages: lastSavedValues.spokenLanguages,
    educatorTypes: lastSavedValues.educatorTypes,
  });

  /**
   * Effects
   */

  // Reset the selected pro when the pros change
  useEffect(() => {
    setSelectedProId(undefined);
  }, [pros.length, prosMap.length]);

  // When the map is visible in desktop, and the search field has found a place, center the map on the place
  useEffect(() => {
    if (searchCoordinates && mapBreakpoint) {
      form.setFieldValue('mapCenter', searchCoordinates);
      form.submitForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapBreakpoint, searchCoordinates]);

  // After submitting the max distance filter, set the zoom and submit the form
  useEffect(() => {
    if (!lastSavedValues.maxDistance) return;
    form.setFieldValue('zoom', DISTANCE_TO_ZOOM_MAP[lastSavedValues.maxDistance]);
    form.submitForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedValues.maxDistance]);

  /**
   * Handlers
   */

  const mapRefreshClickHandler = useCallback(async () => {
    const newValues: ProListPageControllerFormValues = {
      ...form.values,
      maxDistance: undefined,
    };
    form.setValues(newValues);
    await form.submitForm();
    await proSearch.submitWithoutLocation({
      search: { ...getSearchFromValues(newValues), page: 1 },
    });
  }, [form, getSearchFromValues, proSearch]);

  const resetClickHandler = useCallback(() => {
    setSearchFromValues(proMapInitialValues);
    setLastSavedValues(proMapInitialValues);
    form.setValues(proMapInitialValues);
  }, [form, proMapInitialValues, setSearchFromValues]);

  /**
   * Props
   */

  const mapData = useMemo((): ProListPageData['map'] => {
    const { lat, lon } = proSearch?.place ?? {};
    return {
      onMobileShownToggle: showMapModal,
      mobileShown: false,
      lastSavedValues,
      modal: mapModal,
      loading: prosMapLoading,
      selectedLoading: proMapLoading,
      selectedPro: proMap ? formatPros([proMap])[0] : undefined,
      onRefreshClick: mapRefreshClickHandler,
      canRefresh: hasValuesChanged,
      props: {
        onMove: (mapCenter, bounds) => {
          form.setValues({ ...form.values, mapCenter, bounds });
        },
        onAutoPanEnd: async (mapCenter, bounds) => {
          if (bounds == null) return;
          await form.setValues({ ...form.values, mapCenter, bounds });
          form.submitForm();
        },
        onLoad: async (_, mapCenter, bounds) => {
          await form.setValues({ ...form.values, mapCenter, bounds });
          form.submitForm();
        },
        onClick: () => {
          setSelectedProId(undefined);
        },
        onZoomChange: (zoom, bounds) => {
          form.setValues({ ...form.values, zoom, bounds });
        },
        forceCenter: searchCoordinates,
        zoom: lastSavedValues.zoom,
        center: form.values.mapCenter,
        markers: prosMap.filter((pro) => pro.address?.lat && pro.address.lng).map(formatMarker),
        currentMarkerPopup: proMapMarkerLoading ? (
          <MapMarkerLoading color="dark" type="puff" />
        ) : (
          proMapMarker && formatPopup(proMapMarker)
        ),
      },
      ...(lat &&
        lon && {
          center: {
            lat: parseFloat(lat),
            lng: parseFloat(lon),
          },
        }),
    };
  }, [
    form,
    formatMarker,
    formatPopup,
    formatPros,
    hasValuesChanged,
    lastSavedValues,
    mapModal,
    mapRefreshClickHandler,
    proMap,
    proMapLoading,
    proMapMarker,
    proMapMarkerLoading,
    proSearch,
    prosMap,
    prosMapLoading,
    searchCoordinates,
    showMapModal,
  ]);

  const prosClaimed = useMemo(() => {
    return pros.filter((x) => !x.unclaimedProfile);
  }, [pros]);

  const prosUnlaimed = useMemo(() => {
    return pros.filter((x) => x.unclaimedProfile);
  }, [pros]);

  const items = useMemo(() => {
    return formatPros(prosClaimed);
  }, [formatPros, prosClaimed]);

  const imagesToPreload = useMemo(() => {
    return getImagesToPreload(items);
  }, [getImagesToPreload, items]);

  return (
    <>
      <PreloadController images={imagesToPreload} />
      <SeoController {...seoData} canonical={canonicalUrl} />
      <FormikProvider value={form}>
        <ProListPage
          filtersModal={{
            modal: filtersModal,
            onShowClick: showFilters,
            canReset: formValuesChanged,
            loading: prosLoading || prosMapLoading,
            onResetClick: () => {
              const { mapCenter, bounds, zoom } = form.values;
              const newValues = { ...proMapInitialValues, mapCenter, bounds, zoom };
              setSearchFromValues(newValues);
              setLastSavedValues(newValues);
              form.setValues(newValues);
            },
          }}
          onResetClick={resetClickHandler}
          map={mapData}
          items={items}
          unclaimedItems={formatPros(prosUnlaimed)}
          proSearch={proSearch}
          type={type}
          headerTitle={headerTitle ?? ''}
          loading={prosLoading}
          pagination={{
            pageCount,
            forcePage: currentPage,
            onChange: (selected) => {
              handlePaginationChange(selected);
            },
          }}
        />
      </FormikProvider>
    </>
  );
};
